import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getAddressSearch, getMarketListByAddressId } from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { Option } from "tm-shared/types";

import { RHFAutocomplete } from "@/components/RHF/RHFAutocomplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncOptions } from "@/hooks/useAsyncOptions";
import { FieldName } from "@/pages/SV/MarketCreate/MarketCreate.constants";
import {
    setMarketCreatePageModal,
    setMarketCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewMarketFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface AddressSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
    isChecking: boolean;
    setIsChecking: (value: boolean) => void;
}

export const AddressSection = ({
    activeMap,
    resetBelow,
    isChecking,
    setIsChecking,
}: AddressSectionProps) => {
    const dispatch = useAppDispatch();

    const { optionCache } = useAppSelector((s) => s.ui.marketCreatePage);

    const { setValue, getValues, setError, control } =
        useFormContext<NewMarketFormValues>();

    const address = useWatch<NewMarketFormValues>({
        name: "address",
        control,
    });

    const handleAddAddress = useCallback(() => {
        const { region } = getValues();

        const regionOption = optionCache?.region?.find(
            (r) => r.value === region,
        );

        dispatch(
            setMarketCreatePageModal({
                type: MarketCreateModal.AddAddress,
                payload: {
                    region: regionOption?.label ?? "",
                    regionId: regionOption?.value ?? "",
                },
            }),
        );
    }, [dispatch, getValues, optionCache?.region]);

    const [searchQuery, setSearchQuery] = useState("");

    const { options: fetchedOptions, loading: loadingAddress } =
        useAsyncOptions({
            fetchFn: (signal) => {
                const { region } = getValues();

                return getAddressSearch(
                    {
                        name: searchQuery,
                        areaId: region,
                    },
                    { axiosOverrides: { signal } },
                );
            },
            selectData: (response) => Object.entries(response.result),
            mapToOption: ([value, label]) => ({ label, value }),
            dependencies: [searchQuery],
            enabled:
                ((!searchQuery && !address) || searchQuery.length >= 3) &&
                activeMap.address,
            debounceMs: 500,
        });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setMarketCreatePageOptionsCache({
                    fieldName: "address",
                    options: fetchedOptions,
                }),
            );
        }
    }, [dispatch, fetchedOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("address", "");
            resetBelow("address");
        },
        [resetBelow, setValue],
    );

    const checkDuplicateMarkets = useCallback(
        async (option?: Option) => {
            if (!option) return false;

            setIsChecking(true);
            try {
                const { result: marketsFromServer } =
                    await getMarketListByAddressId({
                        addressId: option.value,
                    });

                if (marketsFromServer.length > 0) {
                    dispatch(
                        setMarketCreatePageModal({
                            type: MarketCreateModal.DuplicateByAddress,
                            payload: {
                                markets: marketsFromServer,
                                address: option.label,
                            },
                        }),
                    );
                }
            } catch (error) {
                setError("partnerExternalMarketId", {
                    message: "Помилка при перевірці адреси",
                });

                return "Помилка при перевірці адреси";
            } finally {
                setIsChecking(false);
            }
        },
        [dispatch, setError, setIsChecking],
    );

    const onAfterChangeAddress = useCallback(
        (option?: Option | null) => {
            resetBelow("address");

            if (option) {
                checkDuplicateMarkets(option);
            }
        },
        [checkDuplicateMarkets, resetBelow],
    );

    useEffect(() => {
        const address = getValues("address");

        const currentOption = optionCache.address?.find(
            (o) => o.value === address,
        );

        if (currentOption) void checkDuplicateMarkets(currentOption);
    }, [checkDuplicateMarkets, getValues, optionCache.address]);

    return (
        <RHFAutocomplete
            name="address"
            options={optionCache["address"] ?? []}
            label="Адреса"
            rules={{
                required: validationMessage.required,
            }}
            actionOption={{
                label: "Додати адресу",
                onClick: handleAddAddress,
            }}
            allowClear={false}
            disabled={!activeMap.address || isChecking}
            onAfterChange={onAfterChangeAddress}
            loading={loadingAddress}
            onInputChange={handleInputChange}
            selectProps={{
                size: "large",
            }}
        />
    );
};
